import React from 'react'
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'

export interface PartialDish {
  name: string
  description?: string | null
  price?: number | string | null
  section?: string | null
}

interface MenuScanLoadingScreenProps {
  /**
   * Main status text to display (e.g., "Scanning menu…")
   */
  statusText?: string
  /**
   * Secondary text explaining what's happening (e.g., "Identifying dishes and ingredients.")
   */
  secondaryText?: string
  /**
   * Progress value (0-100) for determinate progress
   */
  progress?: number
  /**
   * Whether to show indeterminate progress (default) or determinate
   */
  isIndeterminate?: boolean
  /**
   * Progressively extracted dishes to show while processing
   */
  partialDishes?: PartialDish[]
  /**
   * When the spinner should be hidden (e.g. an error/terminal state).
   */
  hideSpinner?: boolean
  /**
   * Optional action node rendered below the text — e.g. a retry / "Back to home" button on error.
   */
  action?: React.ReactNode
}

const MenuScanLoadingScreen: React.FC<MenuScanLoadingScreenProps> = ({
  statusText = 'Finding dishes…',
  secondaryText = 'Identifying dishes and ingredients.',
  progress,
  isIndeterminate = true,
  partialDishes = [],
  hideSpinner = false,
  action,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default || '#FAFAFA',
        paddingX: isMobile ? '24px' : '40px',
        paddingY: '60px',
      }}
    >
      {/* Loader Container */}
      {!hideSpinner && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '48px',
          }}
        >
          <CircularProgress
            variant={isIndeterminate ? 'indeterminate' : 'determinate'}
            value={progress ?? 0}
            size={64}
            thickness={2}
            sx={{
              color: theme.palette.primary.main || '#1976D2',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>
      )}

      {/* Status Text */}
      <Typography
        variant="h6"
        component="p"
        sx={{
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: 500,
          lineHeight: 1.5,
          color: theme.palette.text.primary || '#212121',
          marginBottom: '12px',
          textAlign: 'center',
          letterSpacing: '-0.25px',
        }}
      >
        {statusText}
      </Typography>

      {/* Secondary Text */}
      {secondaryText && (
        <Typography
          variant="body2"
          component="p"
          sx={{
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: 400,
            lineHeight: 1.6,
            color: theme.palette.text.secondary || '#757575',
            textAlign: 'center',
            maxWidth: '280px',
            letterSpacing: '-0.2px',
            marginBottom: partialDishes.length > 0 ? 2 : 0,
          }}
        >
          {secondaryText}
        </Typography>
      )}

      {/* Partial dishes list */}
      {partialDishes.length > 0 && (
        <Box
          sx={{
            marginTop: 2,
            maxWidth: '360px',
            width: '100%',
            maxHeight: '40vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {partialDishes.length} dish{partialDishes.length !== 1 ? 'es' : ''} found so far
          </Typography>
          {partialDishes.map((d, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {d.name}
              {d.price != null && d.price !== '' && ` · ${d.price}`}
            </Typography>
          ))}
        </Box>
      )}

      {/* Optional action (e.g. retry / back-to-home on an error state) */}
      {action && <Box sx={{ marginTop: '24px' }}>{action}</Box>}
    </Box>
  )
}

export default MenuScanLoadingScreen
